import React, {useEffect, useState} from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from '@mui/material';
import {format, parseISO} from 'date-fns';
import {enqueueSnackbar} from 'notistack';
import AutoField, {ActionType} from '../../components/AutoForm/AutoFieldNew';
import withAutoFormNew from '../../components/AutoForm/AutoFormNew';
import {useAutoForm} from '../../components/AutoForm/useAutoForm';
import {
  HistoryQueryType,
  useHistoryDiff,
} from '../../components/AccountReviewDialog/useHistoryDiff';
import {OVModelsEnum} from '../../schema-types/globalTypes';
import {HistoryDiffDetails} from '../../schema-types/HistoryDiffDetails';

interface AuditFormValues {
  temporaryId: string;
  temporaryEntityType: OVModelsEnum;
}

export const entityTypes = [
  {label: 'Review', value: 'REVIEW'},
  {label: 'Recommendation', value: 'RECOMMENDATION'},
  {label: 'Pot', value: 'POT'},
  {label: 'Task', value: 'TASK'},
  {label: 'Account', value: 'ACCOUNT'},
  {label: 'Contact', value: 'CONTACT'},
  {label: 'Lead', value: 'LEAD'},
  {label: 'Goal', value: 'GOAL'},
  {label: 'Staff', value: 'STAFF'},
  {label: 'Model Portfolio', value: 'MODEL_PORTFOLIO'},
  {label: 'Note', value: 'NOTE'},
  {label: 'Ovrecommend', value: 'OVRECOMMEND'},
  {label: 'Ovrecommendpot', value: 'OVRECOMMENDPOT'},
];

const AuditForm: React.FC = () => {
  const {values} = useAutoForm<AuditFormValues>();
  const {onGetHistoryDiff, data, loading, error} = useHistoryDiff(
    HistoryQueryType.GENERIC
  );
  const [groupedByDate, setGroupedByDate] = useState<
    Record<string, HistoryDiffDetails[]>
  >({});

  useEffect(() => {
    if (error) {
      enqueueSnackbar({
        message: error.message,
        variant: 'error',
        autoHideDuration: null,
      });
    }
  }, [error]);

  useEffect(() => {
    if (data) {
      const grouped = data.reduce<Record<string, HistoryDiffDetails[]>>((acc, item) => {
        const date = String(item.date) || 'Unknown Date';
        acc[date] = acc[date] || [];
        acc[date].push(item);
        return acc;
      }, {});
      setGroupedByDate(grouped);
    }
  }, [data]);

  const handleSubmit = async () => {
    await onGetHistoryDiff({
      variables: {
        entityId: values.temporaryId,
        entityType: values.temporaryEntityType,
      },
    });
  };

  return (
    <>
      <Box mb={4}>
        <AutoField
          field={{id: 'temporaryId', type: ActionType.text, label: 'id'}}
          needsValue
        />
        <AutoField
          field={{
            id: 'temporaryEntityType',
            type: ActionType.select,
            label: 'Entity Type',
            initialValue: undefined,
            options: entityTypes,
          }}
          data-testid="contact-select"
        />
        <Button variant="contained" disabled={loading} onClick={handleSubmit}>
          {loading ? 'Loading...' : 'Submit'}
        </Button>
      </Box>
      {Object.entries(groupedByDate).map(([date, records]) => (
        <TableContainer component={Paper} key={date} sx={{mb: 2}}>
          <Paper
            sx={{
              borderTop: '3px solid rgb(95, 200, 235)',
              p: 1,
              backgroundColor: '#f5f5f5',
            }}
          >
            <Typography variant="h6" sx={{p: 2, textAlign: 'center'}}>
              Changes made on{' '}
              {date
                ? format(parseISO(date), "EEEE do MMMM yyyy 'at' HH:mm")
                : 'Unknown Date'}{' '}
              by {records[0].user ?? 'Unknown User'}
            </Typography>
          </Paper>
          <Table aria-label="history table">
            <TableHead>
              <TableRow>
                <TableCell>Field</TableCell>
                <TableCell>Old Value</TableCell>
                <TableCell>New Value</TableCell>
                <TableCell>Reason</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((record) => (
                <TableRow key={`${record.field}-${record.date}-${record.newValue}`}>
                  <TableCell>{record.field}</TableCell>
                  <TableCell>{record.oldValue}</TableCell>
                  <TableCell>{record.newValue}</TableCell>
                  <TableCell>{record.reason}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ))}
    </>
  );
};

const AuditFormWrapped = withAutoFormNew<AuditFormValues, object>(AuditForm);

export const AuditPage: React.FC = () => {
  return <AuditFormWrapped formFields={[]} />;
};
